// @flow
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Link } from 'gatsby'
import * as React from 'react'

import { colors, media } from '@toggl/style'

import { Button } from '..'
import * as text from '../text'

type Props = {
  image?: React.Node,
  title: string,
  subtitle?: React.Node,
  link?: React.Node,
  flipped?: boolean,
  noButton?: boolean,
  buttonText?: string,
  noChange?: boolean,
  className?: string,
}

export default function CaseStudy({
  image,
  title,
  subtitle,
  link,
  flipped,
  noButton,
  noChange,
  className,
  buttonText = 'Read case study',
  ...props
}: Props) {
  return (
    <Root
      {...props}
      as={Link}
      to={link}
      flipped={flipped}
      noChange={noChange}
      className={className}
    >
      {image && <Image>{image}</Image>}
      <TextSection>
        <Title>{title}</Title>
        {subtitle && <Subtitle color={colors.fadedPurple}>{subtitle}</Subtitle>}
        {noButton ? null : (
          <CardButton caretDirection="right" color={colors.fadedPurpleAlt}>
            {buttonText}
          </CardButton>
        )}
      </TextSection>
    </Root>
  )
}

const Image = styled.div`
  overflow: hidden;
  border-radius: 10px;
  .gatsby-image-wrapper {
    transition: 0.3s ease-in-out;
    max-width: 110px;
    min-height: 77px;
    ${media.mq[1]} {
      width: 100%;
      max-width: 100%;
    }
  }
`

const TextSection = styled.div`
  color: ${colors.darkPurple};
  flex: 1;
  display: flex;
  flex-direction: column;
  ${media.mq[2]} {
    padding-right: 20px;
  }
`

const CardButton = styled(Button.Secondary)`
  margin-top: 5px;
  ${media.mq[1]} {
    margin-top: auto;
  }
`

const Title = styled(text.H4)`
  margin-bottom: 5px;
  color: ${(p) => p.theme.primary};
  line-height: 1.2 !important;
  ${media.mq[1]} {
    margin-bottom: 8px;
  }
`

const Subtitle = styled(text.P2)`
  ${media.mq[1]} {
    margin-bottom: 30px;
  }
`

const noChangeStyles = ({ noChange }: { noChange?: boolean }) =>
  noChange
    ? css`
        flex-direction: row;
        gap: 15px;
        ${media.mq[1]} {
          margin: 0;
          align-items: flex-start;
        }
        ${media.mq[2]} {
          gap: 20px;
          ${Subtitle} {
            margin-bottom: 0;
          }
          ${TextSection} {
            padding: 0 !important;
          }
        }

        ${Title} {
          ${text.heading5};
        }
      `
    : ''

const Root: React.ComponentType<any> = styled.a`
  margin: 10px 0 35px;
  display: flex;
  gap: 20px;
  align-items: flex-start;
  ${media.mq[1]} {
    align-items: stretch;
    flex: 1;
    gap: 30px;
    ${({ flipped }) =>
      flipped ? 'flex-direction: column;' : 'flex-direction: column-reverse;'};
    ${noChangeStyles};
    &:hover {
      .gatsby-image-wrapper {
        transform: scale(1.1) rotate(3deg);
      }
      button {
        color: ${(p) => p.theme.primary};
      }
    }
  }
`
